/**
 * One list test of a gate (Feature 77 D21): the list it reads, whether any
 * or every item holds or how many do, and the conditions each item is
 * tested by. The item conditions read the item only, never an earlier node,
 * and offer no list test of their own (D22). Another operator that is not a
 * list test, or a field that is not a list, turns it back into a rule.
 */
import type {
  LinkageCountOperator,
  LinkageJoin,
  LinkageListTest,
  LinkageOperator,
  LinkageRule,
} from '@optum/api-client/types';

import { Button } from '../../ui/Button.js';
import { Field } from '../../ui/Field.js';
import { IconButton, Inline, Stack, Typography } from '../../ui/finixt.js';
import { SegmentedControl } from '../../ui/SegmentedControl.js';
import { Select } from '../../ui/Select.js';
import { itemPick, itemTest } from './item-picks.js';
import { COUNT_OPERATORS, isListOperator, itemCondition, withListOperator } from './lists.js';
import { OPERATOR_LABELS, conditionOptions, isLength, offersListTests } from './operators.js';
import { storedOption } from './positions.js';
import { PositionHint } from './PositionHint.js';
import { PICKER_WIDTH, pickerOptions, type ReferenceOption } from './references.js';
import { RuleRow, withOperator, withReference } from './RuleRow.js';
import { ValueInput } from './ValueInput.js';

export function ListTestEditor({
  index,
  test,
  references,
  itemsOf,
  onChange,
  onRemove,
}: {
  index: number;
  test: LinkageListTest;
  references: readonly ReferenceOption[];
  /** What an item condition may read, for the list at a path. */
  itemsOf: (ref: string) => ReferenceOption[];
  onChange: (next: LinkageRule | LinkageListTest) => void;
  onRemove: () => void;
}) {
  const label = `Condition ${index + 1}`;
  const field = references.find((r) => r.value === test.ref);
  const items = itemsOf(test.ref);
  const inner = test.where.items;
  const count = test.op === 'count_where' ? test.count : undefined;
  // The item's type, once the schema describes it: `a list of objects`.
  const itemType = items[0]?.type;
  const setInner = (next: LinkageRule[]) =>
    onChange({ ...test, where: { ...test.where, items: next } });

  return (
    <div
      role="group"
      aria-label={`${label}, a list test`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 7,
        padding: 9,
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-control)',
        background: 'var(--surface-sunken)',
      }}
    >
      <Inline gap={5} align="center" wrap={false}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Select
            searchable
            aria-label={`${label} reference`}
            value={test.ref}
            onChange={(ref) => {
              const type = references.find((r) => r.value === ref)?.type;
              const pick = itemPick(references, ref);
              // A list's item field builds its own test on that list (F82 D9, D10).
              if (pick) onChange(itemTest(pick));
              // A field that is not a list cannot hold a list test: it becomes a rule.
              else if (offersListTests(type)) onChange({ ...test, ref });
              else onChange(withReference({ ref: test.ref, op: 'exists' }, ref, references));
            }}
            panelWidth={PICKER_WIDTH}
            searchPlaceholder="Search a field…"
            options={[
              ...(field ? [] : [storedOption(test.ref, 'not upstream')]),
              ...pickerOptions(references),
            ]}
          />
        </div>
        <IconButton
          icon="close"
          size={28}
          tone="bare"
          aria-label={`Remove condition ${index + 1}`}
          onClick={onRemove}
        />
      </Inline>
      <PositionHint path={test.ref} />
      {itemType && itemType !== 'any' && (
        <Typography variant="meta">a list of {itemType}s</Typography>
      )}
      <Select
        aria-label={`${label} operator`}
        value={test.op}
        onChange={(op) =>
          onChange(
            isListOperator(op)
              ? withListOperator(test, op)
              : withOperator({ ref: test.ref, op: 'exists' }, op as LinkageOperator),
          )
        }
        options={conditionOptions(field?.type, test.op)}
      />
      {count && (
        <Inline gap={5} align="center" wrap={false}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Select
              aria-label={`${label} count comparison`}
              value={count.op}
              onChange={(op) =>
                onChange({ ...test, count: { ...count, op: op as LinkageCountOperator } })
              }
              options={COUNT_OPERATORS.map((op) => ({ value: op, label: OPERATOR_LABELS[op] }))}
            />
          </div>
          <div style={{ width: 88 }}>
            <ValueInput
              label={`${label} count`}
              value={count.value}
              onChange={(value) =>
                onChange({ ...test, count: { ...count, value: value as number } })
              }
            />
          </div>
        </Inline>
      )}
      {count && !isLength(count.value) && (
        <Typography variant="meta" style={{ color: 'var(--warn)' }}>
          A count is a whole number from 0.
        </Typography>
      )}
      <Field id={`gate-item-join-${index}`} label="An item matches when">
        <SegmentedControl
          items={[
            { value: 'all', label: 'All hold' },
            { value: 'any', label: 'Any holds' },
          ]}
          value={test.where.join}
          onChange={(join) =>
            onChange({ ...test, where: { ...test.where, join: join as LinkageJoin } })
          }
        />
      </Field>
      <Stack gap={7}>
        {inner.map((rule, i) => (
          <RuleRow
            key={i}
            index={i}
            label={`${label} item ${i + 1}`}
            missing="not in the item"
            rule={rule}
            references={items}
            onChange={(next) => setInner(inner.map((r, j) => (j === i ? next : r)))}
            onRemove={() => setInner(inner.filter((_, j) => j !== i))}
          />
        ))}
      </Stack>
      {inner.length === 0 && (
        <Typography variant="meta" style={{ color: 'var(--warn)' }}>
          A list test needs an item condition; a save refuses it without one.
        </Typography>
      )}
      <Button
        variant="ghost"
        size="sm"
        icon="add"
        onClick={() => setInner([...inner, itemCondition()])}
      >
        Add an item condition
      </Button>
    </div>
  );
}
